import React from 'react';
import { useQuery } from '@apollo/client';
import { setDefaultPaymentMethod } from '@atlassian/temp-proxies';
import {
  errorNotificationBuilder,
  successNotificationBuilder,
  MoreActions,
  MoreActionsItem,
  OpenSidebarPayload,
  useSidebar,
  WarningDialog,
  useNotification,
  NotificationTypes,
} from '@css-xp/base';
import Button from '@atlaskit/button';
import { defaultBillingProfileSortOptions, maxThresholdForResults } from '@css-xp/ppc-constants';
import { fetchBillingProfilesByPaymentMethodIdQuery } from '@css-xp/billing-v2';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { failureMessage } from '@css-xp/ppc-common';
import { PaymentMethodTypeV2 } from '@css-xp/graphtypes/src/generated-types-new';
import { CARD_PAYMENTS_SIDEBAR } from '../../../common/types';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, useAdvocateAccessControl } from '@css-xp/ppc-access-control';

const sidebarOptions = {
  isOverlay: true,
  sidebarWidth: '600px',
};

export const PayPalPayment: React.FC<{
  txaId: string;
  payPal: any;
  isDesignIteration1Enabled?: boolean;
  onUpdateDone: () => void;
}> = ({ txaId, payPal, onUpdateDone, isDesignIteration1Enabled }) => {
  const { openSidebar, closeSidebar } = useSidebar();
  const paymentMethodId = payPal?.id;
  const isDefaultPaymentMethod = Boolean(payPal?.default);
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false);

  const isPaymentUpdateEnabledForAdvocates = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.UPDATE,
    resource: ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD,
  });

  const isPaymentDeletionEnabledForAdvocates = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.DELETE,
    resource: ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD,
  });

  const { data, loading, error } = useQuery(fetchBillingProfilesByPaymentMethodIdQuery, {
    variables: {
      id: paymentMethodId,
      skip: !paymentMethodId,
      searchOptions: {
        first: maxThresholdForResults, // Disclaimer: Max threshold for the query
        offset: 0,
        sort: defaultBillingProfileSortOptions,
      },
    },
  });
  const billingProfiles = data?.billingProfilesByPaymentMethodId?.billingProfiles || [];
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const getDescription = React.useCallback(() => {
    return (
      <div className="payment-warning-description">
        <p className="pt-8">
          The new default payment method will be the preferred payment method for future purchases.
        </p>
        <p className="pt-8">If you need to change it for current subscriptions, go to the billing profile page.</p>
      </div>
    );
  }, [payPal]);

  const handleSetAsDefaultClick = async () => {
    if (payPal && payPal.id) {
      try {
        await setDefaultPaymentMethod({
          transactionAccountId: txaId,
          id: payPal.id,
        });
        if (!notificationSent) {
          const notificationOptions = successNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withId('update-default-payment-success')
            .withHeader('Update successful')
            .withMessage(`PayPal account set as default payment method.`)
            .withDismissAfter(5000)
            .build();
          sendSuccessNotification(notificationOptions);
          setNotificationSent(true);
        }
        onUpdateDone();
      } catch (err) {
        if (!notificationSent) {
          const errorNotificationOptions = errorNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withHeader('Update unsuccessful')
            .withMessage(<>We're unable to set the payment method as default - {failureMessage()}</>)
            .withId('update-default-payment-error')
            .withDismissAfter(5000)
            .build();
          sendErrorNotification(errorNotificationOptions);
          setNotificationSent(true);
        }
      }
    }
  };

  const paymentMethodActions: MoreActionsItem[] = [
    ...(isPaymentUpdateEnabledForAdvocates
      ? [
          {
            id: 'Set as default',
            content: 'Set as default',
            isDisabled: !!payPal.default,
            action: () => setIsConfirmDialogOpen(true),
          },
          {
            id: 'Replace',
            content: 'Replace',
            action: () =>
              handleSidebarAction({
                sidebarType: CARD_PAYMENTS_SIDEBAR.EDIT_PAYMENT_METHOD,
                payload: {
                  txaId: txaId,
                  paymentMethodId,
                  onCancel: closeSidebar,
                  onUpdateSuccess: closeSidebar,
                  onUpdateFailure: closeSidebar,
                  onUpdateDone,
                  successMessage: 'Payment method replaced',
                  showSubscriptionChargeNote: true,
                  isPaypalSupportEnabled: true,
                  paypalEmail: payPal?.payPal?.email,
                  paymentMethod: PaymentMethodTypeV2.PAYPAL,
                },
                sidebarOptions: isDesignIteration1Enabled ? undefined : sidebarOptions,
              }),
          },
        ]
      : []),
    ...(isPaymentDeletionEnabledForAdvocates
      ? [
          {
            id: 'Remove',
            content: 'Remove',
            action: () =>
              handleSidebarAction({
                sidebarType: CARD_PAYMENTS_SIDEBAR.REMOVE_PAYMENT_METHOD,
                payload: {
                  refetch: onUpdateDone,
                  paymentMethod: payPal,
                  transactionAccountId: txaId,
                  version: isDesignIteration1Enabled ? 2 : undefined,
                  isPayPalPaymentMethod: true,
                  payPalEmailId: payPal?.payPal?.email,
                },
                sidebarOptions: isDesignIteration1Enabled ? undefined : sidebarOptions,
              }),
          },
        ]
      : []),
    {
      id: 'View associated billing profiles',
      content: 'View associated billing profiles',
      action: () =>
        handleSidebarAction({
          sidebarType: CARD_PAYMENTS_SIDEBAR.VIEW_BILLING_PROFILE,
          payload: {
            paymentMethod: payPal,
            billingProfiles: billingProfiles,
            isLoading: loading,
            transactionAccountId: txaId,
            isFromBillingProfile: true,
            error: error,
          },
          sidebarOptions: sidebarOptions,
        }),
    },
  ];

  const renderWarningDialog = () => {
    return (
      <WarningDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={() => {
          setIsConfirmDialogOpen(false);
          handleSetAsDefaultClick();
        }}
        header={'This will be used for future purchases'}
        description={getDescription()}
        okLabel={'Confirm'}
        cancelLabel={'Close'}
        testId={'confirm-dialog-modal'}
      />
    );
  };

  const renderMoreActions = () => <MoreActions actions={paymentMethodActions} testId="payment-method-actions-btn" />;

  return (
    <div
      key={`ppc-paypal-payment-method-${payPal?.payPal?.name}`}
      className="ppc-payment-method-line-item"
      data-testid="ppc-payment-methods-line-item"
    >
      <div className="card-details" data-testid={`payment-card-detail`}>
        <PaypalPaymentMethod
          emailID={payPal?.payPal?.email}
          withBorder={false}
          renderChildrenInline={true}
          isDesignIteration1Enabled={isDesignIteration1Enabled}
        />
      </div>
      {isDefaultPaymentMethod && (
        <Button appearance="subtle" isDisabled>
          Default
        </Button>
      )}
      {renderMoreActions()}
      {renderWarningDialog()}
    </div>
  );
};
